import axios from '@fs/zion-axios'

const BASE_ARTIFACTS_PATH = '/service/dmc/artifacts'

const formatArtifact = (artifact) => ({
  ...artifact,
  id: artifact.id || artifact.ID,
})

export async function getPatronArtifacts(
  paginatorCount,
  paginatorOffset,
  includeAssociatedArtifacts,
  artifactCategory,
  patronId,
  status
) {
  let url = `${BASE_ARTIFACTS_PATH}/patrons/`
  let params = {
    maxRecords: paginatorCount,
    firstRecord: paginatorOffset,
  }

  if (status === 'tombstoned') {
    url += `tombstones/${patronId}`
    params = {
      maxRecords: 500,
      firstRecord: 0,
    }
  } else {
    if (status === 'restricted') {
      params.filterScreeningState = 'RESTRICTED'
    } else if (status === 'approved') {
      params.filterScreeningState = 'APPROVED'
    }
    params = { ...params, includeAssociatedArtifacts, artifactCategory }
    url += `${patronId}/artifacts`
  }

  const response = await axios.get(url, { params })
  response.data?.artifact && (response.data.artifact = response.data.artifact.map(formatArtifact))
  return response
}

export function getPatronByCisId(cisId) {
  return axios.get(`${BASE_ARTIFACTS_PATH}/patrons/patronsByCisId/${cisId}`)
}

export function getPatronByPatronId(patronId) {
  return axios.get(`${BASE_ARTIFACTS_PATH}/patrons/${patronId}`)
}

export function getPatronsByPatronIds(params) {
  return axios.get(`${BASE_ARTIFACTS_PATH}/patronsByPatronIds`, { params })
}

export function getArtifactEvents(artifactId) {
  return axios.get(`${BASE_ARTIFACTS_PATH}/${artifactId}/events`)
}

export function getArtifactTopicTags(artifactId) {
  return axios.get(`${BASE_ARTIFACTS_PATH}/topicTags/${artifactId}/topics`)
}

export async function getArtifact(artifactId) {
  const artifact = await axios.get(`${BASE_ARTIFACTS_PATH}/${artifactId}`)
  artifact.data && (artifact.data = formatArtifact(artifact.data))
  return artifact
}

export async function getTombstonedArtifact(artifactId, patronId) {
  const artifact = await axios.get(`${BASE_ARTIFACTS_PATH}/tombstoned/${patronId}/${artifactId}`)
  artifact.data && (artifact.data = formatArtifact(artifact.data))
  return artifact
}

export async function getArtifacts(uploadDate, artifactCategory, maxRecords, firstRecordIndex, language) {
  const params = {
    uploadDate,
    artifactCategory: artifactCategory.toUpperCase(),
    maxRecords,
    firstRecord: firstRecordIndex,
    // If all languages requested, don’t set that filter
    language: language === 'all' ? undefined : language,
  }
  const response = await axios.get(BASE_ARTIFACTS_PATH, { params })
  response.data?.artifact && (response.data.artifact = response.data.artifact.map(formatArtifact))
  return response
}

export async function getEscalatedArtifacts(pageSize) {
  const params = {
    pageSize,
  }
  const response = await axios.get(`${BASE_ARTIFACTS_PATH}/escalated`, { params })
  response.data?.artifact && (response.data.artifact = response.data.artifact.map(formatArtifact))
  return response
}

export function updateArtifact(updatedArtifact, sendEmail) {
  const params = {
    sendEmail,
  }
  return axios.post(`${BASE_ARTIFACTS_PATH}/${updatedArtifact.ID}`, updatedArtifact, { params })
}

export function deleteArtifact(artifactId) {
  return axios.delete(`${BASE_ARTIFACTS_PATH}/${artifactId}`)
}

export function deleteTombstonedArtifact(artifactId, patronId) {
  return axios.delete(`${BASE_ARTIFACTS_PATH}/tombstoned/${patronId}/${artifactId}`)
}

export function restoreArtifact(artifactId, patronId) {
  return axios.post(`${BASE_ARTIFACTS_PATH}/tombstoned/${patronId}/${artifactId}`)
}

export function restrictAllPatronArtifacts(patronId, sendEmail) {
  const params = {
    sendEmail,
  }
  return axios.post(`${BASE_ARTIFACTS_PATH}/patrons/${patronId}/artifacts`, {}, { params })
}

export function deleteAllPatronArtifacts(patronId) {
  return axios.delete(`${BASE_ARTIFACTS_PATH}/patrons/${patronId}/artifacts`)
}

export async function getArtifactsByKeyword(searchTerms, searchCategory, paginatorCount, paginatorOffset) {
  const params = {
    searchTerms,
    searchCategories: searchCategory,
    maxRecords: paginatorCount,
    firstRecord: paginatorOffset,
  }
  const response = await axios.get(`${BASE_ARTIFACTS_PATH}/artifactsByKeyword`, { params })
  response.data?.artifact && (response.data.artifact = response.data.artifact.map(formatArtifact))
  return response
}

export async function getAttachedArtifacts(personId, artifactCategory, firstRecord, maxRecords) {
  const params = {
    artifactCategory,
    maxRecords,
    firstRecord,
    community: true,
    includeDatesPlaces: true,
    includeTotal: true,
  }
  const response = await axios.get(`${BASE_ARTIFACTS_PATH}/attachedArtifacts/${personId}`, { params })
  response.data?.artifact && (response.data.artifact = response.data.artifact.map(formatArtifact))
  return response
}

export async function getArtifactGroups(artifactId) {
  const response = await axios.get(`${BASE_ARTIFACTS_PATH}/groups/${artifactId}`)
  return response
}

export function deleteTopicTag(topicTagId) {
  return axios.delete(`${BASE_ARTIFACTS_PATH}/topicTags/${topicTagId}`)
}
